/**
 * Admin V3 — Toast visual component
 *
 * Presentation only. Lifecycle (auto-dismiss, replacement, queueing) is
 * owned by useToast / ToastProvider. This component just renders the
 * current toast payload.
 *
 * Style mirrors M2B's BookingsTab.js ToastSlot: emerald-success / rose-error,
 * fixed bottom-right, with a manual close X.
 */

import React from 'react';
import { CheckCircle2, AlertCircle, X, Info } from 'lucide-react';
import { cn } from '../visualTokens';

const TOAST_STYLES = {
  success: {
    container: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    iconColor: 'text-emerald-500',
    closeColor: 'text-emerald-600',
    Icon: CheckCircle2,
  },
  error: {
    container: 'bg-rose-50 border-rose-200 text-rose-800',
    iconColor: 'text-rose-500',
    closeColor: 'text-rose-600',
    Icon: AlertCircle,
  },
  info: {
    container: 'bg-slate-50 border-slate-200 text-slate-800',
    iconColor: 'text-slate-500',
    closeColor: 'text-slate-600',
    Icon: Info,
  },
};

const Toast = ({ toast, onClose }) => {
  if (!toast) return null;
  const style = TOAST_STYLES[toast.type] || TOAST_STYLES.info;
  const { Icon } = style;

  return (
    <div
      className="fixed bottom-6 right-6 z-50 max-w-md"
      role="status"
      aria-live="polite"
      data-testid={`adminv3-toast-${toast.type}`}
    >
      <div className={cn('flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg', style.container)}>
        <Icon className={cn('h-5 w-5 mt-0.5 flex-shrink-0', style.iconColor)} />
        <div className="flex-1 text-sm font-medium break-words">
          {toast.message}
        </div>
        <button
          type="button"
          onClick={onClose}
          className={cn('flex-shrink-0 rounded p-0.5 hover:bg-white/40', style.closeColor)}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default Toast;
